<template>
  <div v-if="!loading" :class="['edit-invoice-design-wrap', { 'on-modal': onModal }]">

    <div v-if="showControls" class="left-col">
      <overlay-scrollbars :options="{ scrollbars: { autoHide: 'never' }, overflowBehavior: { y: 'scroll', x: 'hidden' }, className : 'os-theme-dark os-theme-gekko' }">
        <div class="accordion invoice-design-sidebar-accordion" role="tablist">

          <b-card no-body>
            <b-card-header header-tag="header" role="tab">
              <b-button block v-b-toggle.accordion-1 class="accordion-btn">
                <span class="premium-badge--inline">
                  <font-awesome-icon icon="fa-solid fa-layer-group" /> {{ $gettext('Layout') }}
                  <GkBadge 
                    icon="fa-solid fa-crown"
                    icon-only
                    type="premium"
                  />
                </span>
                <font-awesome-icon icon="fa-solid fa-chevron-down" class="chevron-icon" />
              </b-button>
            </b-card-header>
            <b-collapse id="accordion-1" visible accordion="invoice-design-sidebar-accordion-1" role="tabpanel">
              <b-card-body>
                <b-card-text class="mb-4">
                  {{ $gettext('Selected template will be applied to your next invoices and quotations.') }}
                </b-card-text>

                <b-card-text>
                  <div class="layout-selector">
                    <b-form-group v-slot="{ ariaDescribedby }" :disabled="loadingPDF">
                      <b-form-radio-group class="layout-selector-radio-group">
                        <b-form-radio 
                          v-for="(item, index) in defaultLayouts" :key="'template-' + index"
                          v-model="selectedTemplateId"
                          :aria-describedby="ariaDescribedby" 
                          :value="item.template"
                          @change="switchLayout"
                          :class="{ selected: (item.template === selectedTemplateId), loading: loadingPDF }"
                        >
                          <div class="layout-item-wrap">
                            <div class="layout-img-wrap">
                              <img :src="`/static/img/invoice${ (item.template + 1) }.png`">
                            </div>
                            {{ item.name }}
                          </div>
                        </b-form-radio>
                      </b-form-radio-group>
                    </b-form-group>
                  </div>
                  
                  <GekkoButton 
                    @click="resetToDefault()"
                    :text="$gettext('Reset to default')"
                    :icon="'fa-solid fa-xmark'"
                    variant="link"
                    size="sm"
                    :disabled="loadingPDF"
                  />
                </b-card-text>
              </b-card-body>
            </b-collapse>
          </b-card>

          <b-card no-body style="overflow: visible;">
            <b-card-header header-tag="header" role="tab">
              <b-button block v-b-toggle.accordion-4 class="accordion-btn">
                <span>
                  <font-awesome-icon icon="fa-solid fa-cogs" /> {{ $gettext('Configuration') }}
                </span>
                <font-awesome-icon icon="fa-solid fa-chevron-down" class="chevron-icon" />
              </b-button>
            </b-card-header>
            <b-collapse id="accordion-4" visible accordion="invoice-design-sidebar-accordion-4" role="tabpanel">
              <b-card-body>
                <b-card-text>
                  <b-form-checkbox v-model="previewTemplate.pagination" @change="updatePaginationOption" class="card-config-item" switch>
                    <div id="tt-pagination">
                      <span class="title premium-badge--inline">{{ $gettext('Add pagination') }}
                        <GkBadge 
                          icon="fa-solid fa-crown"
                          icon-only
                          type="premium"
                        />
                      </span>
                    </div>
                    <b-tooltip :custom-class="'tooltip-padded'" target="tt-pagination" placement="top" triggers="hover">
                      <div class="tooltip-text">
                        {{ $gettext('Pagination will also appear on single page invoices as 1/1') }}
                      </div>
                    </b-tooltip>
                  </b-form-checkbox>
                  <b-form-checkbox 
                    v-if="currentCompany.country == 'ES'" 
                    v-model="showVerifactuPreview" 
                    @change="onVerifactuPreviewChange" 
                    v-b-tooltip.hover 
                    :title="verifactuTooltipTitle"
                    class="card-config-item checkbox-bg-color custom-checkbox-valign" switch>
                      <img style="width: 100px" src="/static/img/verifactu-logo.png">
                  </b-form-checkbox>
                </b-card-text>
              </b-card-body>
            </b-collapse>
          </b-card>

          <b-card no-body style="overflow: visible;">
            <b-card-header header-tag="header" role="tab">
              <b-button block v-b-toggle.accordion-2 class="accordion-btn">
                <span class="premium-badge--inline">
                  <font-awesome-icon icon="fa-solid fa-font" /> {{ $gettext('Fonts') }}
                  <GkBadge 
                    icon="fa-solid fa-crown"
                    icon-only
                    type="premium"
                  />
                </span>
                <font-awesome-icon icon="fa-solid fa-chevron-down" class="chevron-icon" />
              </b-button>
            </b-card-header>
            <b-collapse id="accordion-2" :visible="!onModal" accordion="invoice-design-sidebar-accordion-2" role="tabpanel">
              <b-card-body>
                <b-card-text>
                  <FontSelector :preselect="previewTemplate.font" @selected-font="updateSelectedFont" :disabled="loadingPDF" />
                </b-card-text>
              </b-card-body>
            </b-collapse>
          </b-card>

          <b-card no-body>
            <b-card-header header-tag="header" role="tab">
              <b-button block v-b-toggle.accordion-3 class="accordion-btn">
                <span class="premium-badge--inline">
                  <font-awesome-icon icon="fa-solid fa-palette" /> {{ $gettext('Colors') }}
                  <GkBadge 
                    icon="fa-solid fa-crown"
                    icon-only
                    type="premium"
                  />
                </span>
                <font-awesome-icon icon="fa-solid fa-chevron-down" class="chevron-icon" />
              </b-button>
            </b-card-header>
            <b-collapse id="accordion-3" :visible="!onModal" accordion="invoice-design-sidebar-accordion-3" role="tabpanel">
              <b-card-body>
                <b-card-text>
                  <b-form-group label-cols="6" label-size="sm" label="Primary" label-for="input-sm">
                    <b-form-input type="color" class="input-color" size="sm" v-model="previewTemplate.color1" :disabled="loadingPDF" />
                    <span class="picker-hex" :style="{ color: getHighContrastColor(previewTemplate.color1) }">{{ previewTemplate.color1 }}</span>
                  </b-form-group>
                  <b-form-group label-cols="6" label-size="sm" label="Secondary" label-for="input-sm">
                    <b-form-input type="color" class="input-color" size="sm" v-model="previewTemplate.color2" :disabled="loadingPDF" />
                    <span class="picker-hex" :style="{ color: getHighContrastColor(previewTemplate.color2) }">{{ previewTemplate.color2 }}</span>
                  </b-form-group>
                  <GekkoButton 
                    @click="applyColors()"
                    :text="$gettext('Apply to preview')"
                    :icon="'fa-solid fa-check'"
                    variant="link"
                    size="sm"
                    :disabled="loadingPDF"
                  />
                </b-card-text>
              </b-card-body>
            </b-collapse>
          </b-card>


        </div>
      </overlay-scrollbars>
    </div>
    <div :class="['right-col pdf-preview-wrap', { 'not-modal': !onModal, 'w-100': !showControls }]">
      <LoadingSpinner v-if="loadingPDF" :display-text="$gettext('Generating pdf')" />

      <!-- <iframe v-if="pdfFile" class="pdf-preview" :class="{ fetching: loadingPDF }" :src="pdfFile + '#toolbar=0&view=' + (onModal ? '' : 'Fit')"></iframe> -->
      
      <iframe v-if="!forceMobileViewer && !showMobileViewer && pdfFile" id="pdf-frame" class="pdf-preview" :class="{ fetching: loadingPDF }" :src="pdfFile + '#toolbar=0&view=' + (onModal ? '' : 'Fit')"></iframe>
      <GkPDFViewer v-if="(forceMobileViewer || showMobileViewer) && pdfFile" :item-pdf="pdfFile" />


    </div>
  </div>
</template>

<script>
  import { mapGetters, mapActions, mapMutations } from "vuex";
  import { getHighContrastColor } from "@/utils/"

  import pdfviewer from "@/mixins/pdfviewer";

  import TitleBar from "@/components/TitleBar";
  import GekkoButton from "@/components/GekkoButton";
  import ModalBase from "@/components/base_components/ModalBase"
  import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"

  import FontSelector from "@/components/invoices/ui/design/FontSelector"
  import GkPDFViewer from '@/components/invoices/ui/GkPDFViewer'
  import InvoiceLayoutService from "@/store/services/invoice.layout.service.js"

  export default {
    name: "EditDesign",
    mixins: [pdfviewer],
    components: {
      ModalBase,
      TitleBar,
      GekkoButton,
      LoadingSpinner,
      FontSelector,
      GkPDFViewer
    },
    props: {
      invoice: {
        type: Object,
        default: () => {}
      },
      totals: {
        type: Object,
        default: () => {}
      },
      recipient: {
        type: Number,
        default: null
      },
      onModal: {
        type: Boolean,
        default: false
      },
      showControls: {
        type: Boolean,
        default: true
      }
    },
    data () {
      return {
        loading: true,
        loadingPDF: true,
        pdfFile: null,
        selectedTemplateId: null,
        previewTemplate: {},
        showVerifactuPreview: false
      }
    },
    computed: {
      ...mapGetters({ 
        currentCompany: "main/getCurrentCompany",
        currentLayouts: "invoicesLayout/getCurrentLayouts",
        defaultLayouts: "invoicesLayout/getDefaultLayouts",
        currentLayout: "invoicesLayout/getCurrentLayout",
        defaultLayoutById: "invoicesLayout/getDefaultLayoutById",
        defaultLayoutValuesById: "invoicesLayout/getDefaultLayoutValuesById",
        availableBrands: "brands/getAvailableBrands",
        selectedBrand: "brands/getSelectedBrand",
      }),
      selectedDefaultConfig () {
        return this.defaultLayoutById(this.selectedTemplateId)
      },
      verifactuTooltipTitle() {
      return this.showVerifactuPreview ? 
          this.$gettext('You are viewing an invoice with a QR code as it would be sent to the AEAT. Uncheck this option to preview regular invoices. This will not affect existing invoices.')
          : this.$gettext('You are viewing a regular invoice without a QR code. Check this option to preview invoices as they would be sent to the AEAT, including the QR code. This will not affect existing invoices.')
      }
    },
    created () {
      this.loading = true
      this.loadLayoutData().then(() => {
        this.loading = false
        this.showVerifactuPreview = this.currentCompany.companysettings.make_invoice_verifactu
        this.setupData(this.currentLayout)
      })
    },
    mounted () {
      if (this.onModal) document.body.classList.add('modal-on')
    },
    methods: {
      getHighContrastColor,
      ...mapActions ({
        loadLayoutData: "invoicesLayout/loadLayoutData",
        saveLayout: "invoicesLayout/saveLayout",
        updateLayout: "invoicesLayout/updateLayout",
        refreshCompanyData: "main/refreshCompanyData"
      }),
      ...mapMutations({
        setWaitingForResponse: "main/SET_WAITING_FOR_RESPONSE"
      }),
      setupData (target_layout) {
        if (this.selectedBrand) {
          // brand editing
          target_layout = this.currentLayouts.filter(layout => layout.id === this.selectedBrand.invoice_layout)[0]
          this.previewTemplate = {}
          this.previewTemplate.id = target_layout.id
          this.previewTemplate.company = target_layout.company
          this.previewTemplate.status = 'brand'
          this.previewTemplate.brand_id = this.selectedBrand.id
          this.previewTemplate.color1 = target_layout.color1
          this.previewTemplate.color2 = target_layout.color2
          this.previewTemplate.pagination = target_layout.pagination
          this.previewTemplate.template = target_layout.template
          this.previewTemplate.font = target_layout.font
          this.previewTemplate.name = target_layout.name
          this.selectedTemplateId = this.previewTemplate.template
        } else {
          // invoice design
          if (this.invoice.brand && this.invoice.layout) {
            this.previewTemplate = {}
            this.previewTemplate.id = this.invoice.layout.id
            this.previewTemplate.company = this.invoice.layout.company
            this.previewTemplate.color1 = this.invoice.layout.color1
            this.previewTemplate.color2 = this.invoice.layout.color2
            this.previewTemplate.pagination = this.invoice.layout.pagination
            this.previewTemplate.template = this.invoice.layout.template
            this.previewTemplate.font = this.invoice.layout.font
            this.previewTemplate.status = this.invoice.layout.status
            this.selectedTemplateId = this.previewTemplate.template
          } else {
            this.previewTemplate = {}
            this.previewTemplate.id = target_layout.id
            this.previewTemplate.company = target_layout.company
            this.previewTemplate.color1 = target_layout.color1
            this.previewTemplate.color2 = target_layout.color2
            this.previewTemplate.pagination = target_layout.pagination
            this.previewTemplate.template = target_layout.template
            this.previewTemplate.font = target_layout.font
            this.previewTemplate.status = target_layout.status
            this.previewTemplate.name = target_layout.name // is this necessary?
            this.selectedTemplateId = this.previewTemplate.template
          }
        }
        this.getPreviewPdf(this.previewTemplate, true)
      },
      getPreviewData() {
        let invoice = this.invoice
        invoice.invoice_lines.forEach((line) => {
          if (!line.amount) {
            const index = invoice.invoice_lines.indexOf(line)
            if (index > -1) {
              invoice.invoice_lines.splice(index, 1)
            }
          }
        })
        return invoice
      },
      getPreviewPdf (layout, init = false) {
        this.loadingPDF = true
        if (!init) {
          this.$emit('changed', false)
        }
        const invoice = this.getPreviewData()

        const layoutData = { 
          invoice: invoice,
          totals: this.totals,
          layout: layout,
          recipient: this.recipient,
          verifactu_preview: this.showVerifactuPreview
        }

        InvoiceLayoutService.previewPdf(layoutData).then((response) => {
          this.pdfFile = response
          this.loadingPDF = false
          this.$emit('pdf-loaded', false)
        })
      },
      switchLayout () {
        this.previewTemplate.template = this.selectedTemplateId;
        this.resetToDefault()
      },
      saveChanges () {
        if (!this.currentCompany.is_premium) {
          this.$store.commit('togglePremiumModal', 'web_invoices_design')
          return
        }

        this.setWaitingForResponse(true)
        if (this.previewTemplate.id) {
          console.log("EDIT");
          this.updateLayout(this.previewTemplate).then((response) => {
            this.$store.commit('setAlert', {
              title: this.$gettext("Invoice design"),
              text: this.$gettext("Invoice design updated successfully."),
              variant: 'success',
              duration: 4
            })
            this.$emit('saved', false)
          }).finally(() => {
            this.setWaitingForResponse(false)
          })
        } else {
          console.log("CREATE");
          this.saveLayout(this.previewTemplate).then(() => {
            this.$store.commit('setAlert', {
              title: this.$gettext("Invoice design"),
              text: this.$gettext("Invoice design updated successfully."),
              variant: 'success',
              duration: 2
            })
            this.$emit('saved', false)
          }).finally(() => {
            this.setWaitingForResponse(false)
          })
        }
      },
      updateSelectedFont (selected_font) {
        if (this.previewTemplate.font !== selected_font) {
          this.previewTemplate.font = selected_font
          this.getPreviewPdf(this.previewTemplate)
        }
      },
      updatePaginationOption () {
        this.getPreviewPdf(this.previewTemplate)
      },
      resetToDefault () {
        const default_values = this.defaultLayoutValuesById(this.previewTemplate.template)
        for (const prop in default_values) {
          if (Object.prototype.hasOwnProperty.call(this.previewTemplate, prop)) {
            this.previewTemplate[prop] = default_values[prop]
          }
        }
        this.getPreviewPdf(this.previewTemplate)
      },
      applyColors () {
        this.getPreviewPdf(this.previewTemplate)
      },
      onVerifactuPreviewChange () {
        this.getPreviewPdf(this.previewTemplate, true)
      }
    }
  }
</script>

<style lang="scss">
.edit-invoice-design-wrap {
  .custom-radio {
    .custom-control-label {
      &:before, &:after {
        display: none;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
  .edit-invoice-design-wrap {
    display: flex;
    width: 100%;
    height: 100%;
    .left-col {
      background: white;
      padding: 0;
      border-right: 1px solid $colorBorderDash;
      .os-host {
        height: 100%;
      }
      .invoice-design-sidebar-accordion {
        .card {
          border: 0;
          border-radius: 0;
          border-bottom: 1px solid $colorBorderDash;

          .layout-selector {
            .layout-selector-radio-group {
              display: flex;
              flex-wrap: wrap;
              justify-content: center;
              gap: 1rem;
            }
            .custom-radio {
              margin: 0;
              padding: 0;
              transition: all 0.3s ease;
              &.loading:not(.selected) { opacity: 0.5; }
              .layout-item-wrap {
                cursor: pointer;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                .layout-img-wrap {
                  border-radius: $radiusSm;
                  border: 2px solid #e9e9e9;
                  overflow: hidden;
                  img { 
                    margin: -1px; // fix for hiding img border
                    width: 70px; 
                  }
                }
                
              }
              &.selected { 
                .layout-item-wrap {
                  color: $colorBrand;
                  .layout-img-wrap {
                    border: 2px solid $colorBrand;
                  }
                }  
              }

            }
          }
          .picker-hex {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 14px;
            pointer-events: none;
          }

          .card-header {
            padding: 0;
            background: $colorBgSidebar;
            border-bottom: 1px solid $colorBorderDash;
            > .accordion-btn {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 1rem;
              border: 0;
              background: transparent;
              border-radius: 0;
              color: $colorTextBase;
              text-align: left;
              .chevron-icon {
                font-size: 13px;
              }
              &.not-collapsed {
                .chevron-icon {
                  transform: rotate(-180deg);
                }
              }
              // &:hover,
              &.not-collapsed {
                background: white;
              }
            }
          }
          .card-body {
            padding: 1rem;

            .card-config-item {
              padding-top: 0.5rem;
              padding-bottom: 0.5rem;
            }
          }
        }
      }
    }
    .pdf-preview-wrap {
      padding: 0;
      &.not-modal {
        aspect-ratio: 1 / 1.4142; // for portrait A4
        // max-width: 800px; // old styling
      }
      .gk-spinner-title {
        color: $colorBrand;
      }
      .pdf-preview {
        height: 100%;
        width: 100%;
        border: 0;
        transition: opacity 0.3s ease;
        &.fetching { opacity: 0.5; }
      }
    }
    .left-col {
      position: relative;
      width: 100%;
      height: 50%;
    }
    .right-col {
      position: relative;
      width: 100%;
      height: 50%;
    }
    flex-direction: column;
    @media (min-width: '768px') {
    flex-direction: row;
      .left-col {
        width: 350px;
        height: 100%;
      }
      .right-col {
        width: calc(100% - 300px);
        height: 100%;
      }
    }
  }
</style>